import * as _ from 'lodash';
import { PayloadServiceHelper } from '../orchestration/payload-service.helper';

export class AnalysisViewSwitcherHelper {
  static async switchAnalysisView(comp: any, view: any, block?: any) {
    const targetBlock = block || comp.chatPreviewBlocks.find((b: any) => b.messageId === comp.selectedMessageId);

    // Ensure we use the isolated historical payload if available
    const blockMemberResponses = targetBlock?.analyticalData?.member_responses || comp.allMemberResponses;

    if (!blockMemberResponses) {
      console.warn('[MULTI-VIEW-DEEP-DEBUG] No responses available to switch.');
      return;
    }

    const currentMcode = targetBlock ? targetBlock.mcode : comp.mcode;
    if (currentMcode === view.mcode) {
      return;
    }

    const isLatestBlock = targetBlock && comp.chatPreviewBlocks.length > 0 && targetBlock.messageId === comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].messageId;
    if (comp.isHistorySaving && isLatestBlock) {
      comp.showToast('Please wait while the current analysis is generating.', 'info');
      return;
    }

    if (targetBlock) {
      if (!comp.isHistorySaving) comp.selectedMessageId = targetBlock.messageId;
      comp.setFailedCaseState(false, targetBlock);
    }

    const msgId = targetBlock?.messageId || comp.selectedMessageId || 'current';

    let result = blockMemberResponses[view.key];
    if (view.mcode === 2 && !result) {
      result = blockMemberResponses.brandanalysis || blockMemberResponses.brand_analysis;
    }

    // Try to pre-populate cache from history if it exists!
    const esKey = view.mcode === 1 ? 'getSnapshotOutput' : 'getConfiguration';

    if (result && result[esKey] && !comp.multiViewDataCache[msgId]?.[view.mcode]) {
      const historyData = result[esKey];
      const parsedData = typeof historyData === 'string' ? JSON.parse(historyData) : historyData;

      // Safety check: Make sure we didn't accidentally save the corrupted OutputdataObject format in the past
      // Real API payload always has 'Data' wrapped or is an array.
      if (Array.isArray(parsedData) || parsedData.Data || parsedData.Row === undefined || view.mcode === 1) {

        // [FIX] Inject trendline data from siblings for Snapshot (mcode=1)
        if (view.mcode === 1) {
          const analyticalSource = targetBlock?.analyticalData || targetBlock?.parsedData;
          const sourceForTrend = result?.trendline_response ? result :
            (analyticalSource?.trendline_response ? analyticalSource :
              (targetBlock?.filteredWidgetData?.trendline_response ? targetBlock.filteredWidgetData :
                (targetBlock?.builtCustomData?.trendline_response ? targetBlock.builtCustomData :
                  (analyticalSource?.GetCustomSnapshotWidget?.trendline_response ? analyticalSource.GetCustomSnapshotWidget : null))));

          if (sourceForTrend?.trendline_response && !parsedData.trendline_response) {
            parsedData.trendline_response = sourceForTrend.trendline_response;
          } else if (comp.currentTrendlineResponse && !parsedData.trendline_response) {
            parsedData.trendline_response = comp.currentTrendlineResponse;
          }

          if (sourceForTrend?.response_payload_trendline && !parsedData.response_payload_trendline) {
            parsedData.response_payload_trendline = sourceForTrend.response_payload_trendline;
          } else if (comp.currentRowPayloadTrendline && !parsedData.response_payload_trendline) {
            parsedData.response_payload_trendline = comp.currentRowPayloadTrendline;
          }

          const sourceForPayload = result?.response_payload ? result :
            (analyticalSource?.response_payload ? analyticalSource :
              (targetBlock?.filteredWidgetData?.response_payload ? targetBlock.filteredWidgetData :
                (targetBlock?.builtCustomData?.response_payload ? targetBlock.builtCustomData :
                  (analyticalSource?.GetCustomSnapshotWidget?.response_payload ? analyticalSource.GetCustomSnapshotWidget : null))));

          if (sourceForPayload?.response_payload && !parsedData.response_payload) {
            parsedData.response_payload = sourceForPayload.response_payload;
          }
        }

        if (!comp.multiViewDataCache[msgId]) comp.multiViewDataCache[msgId] = {};
        comp.multiViewDataCache[msgId][view.mcode] = parsedData;
      } else {
        console.warn(`[MULTI-VIEW] Discarded corrupted history data for mcode: ${view.mcode}`);
      }
    }

    const hasDataCached = !!comp.multiViewDataCache[msgId]?.[view.mcode];
    const hasInsightsCached = !!(comp.multiViewInsightsCache[msgId] || comp.multiViewInsightsCache['current'])?.[view.mcode];

    // Only show global loader if we truly need to wait for Step 2 payload
    if (!hasDataCached) comp._loaderService.showLoader();

    const isSwitchingOldBlockDuringLoad = comp.isHistorySaving && targetBlock && comp.chatPreviewBlocks.length > 0 && targetBlock.messageId !== comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].messageId;
    try {
      const prevMcode = comp.mcode;
      if (targetBlock) {
        targetBlock.isAnalysisSwitching = true; // Trigger exit/reset
      } else {
        comp.isAnalysisSwitching = true;
      }

      // 1. STATE RESET: Prevent overlap (skip if this is an old block while a new one is generating)
      if (!isSwitchingOldBlockDuringLoad) {
        comp.insightsVal = [];
        comp.insightsStreamingText = '';
        comp.isInsightsStreaming = false;
        comp.updateMarkdownTextRealTime('');
        comp.isInsightspopupOpen = false;
        comp.activeView = view;
        comp.mcode = view.mcode;
      }
      if (targetBlock) {
        targetBlock.activeView = view;
        targetBlock.mcode = view.mcode;
        targetBlock.userSelectedMcode = true;
      }
      comp.chatPreviewBlocks = [...comp.chatPreviewBlocks];

      // Only show AI loader if insights aren't ready yet
      if (!hasInsightsCached && !isSwitchingOldBlockDuringLoad) comp.startAiLoader();

      if (!result) throw new Error(`Result not found for ${view.name}`);

      // Reconstruct payload and trigger Step 2 data fetch
      comp.chatPayload = comp.constructSpecialized(view.mcode, result);

      let opData: any;

      // Check for pre-fetched analytical data (Step 2 responses)
      const dataCache = comp.multiViewDataCache[msgId] || comp.multiViewDataCache['current'];

      if (dataCache && dataCache[view.mcode]) {
        opData = dataCache[view.mcode];
        if (targetBlock) {
          targetBlock.isAnalysisSwitching = false; // Trigger entrance animation
        } else {
          comp.isAnalysisSwitching = false;
        }
      } else {
        try {
          const freshToken = await comp.getBearerToken();
          if (freshToken) {
            comp.chatPayload.bearer_token = freshToken;
            comp.currentOrchToken = freshToken;
          }
        } catch (e) {
          console.warn('[MULTI-VIEW] Failed to refresh token before fallback fetch', e);
        }

        if (view.mcode === 1) opData = await comp.apiService.snapshotPayloadAPI(comp.chatPayload).toPromise();
        else if (view.mcode === 2) opData = await comp.apiService.brandAnalysisPayloadAPI(comp.chatPayload).toPromise();
        else if (view.mcode === 3) opData = await comp.apiService.crosstabPayloadAPI(comp.chatPayload).toPromise();

        // Store in cache for future switches
        if (opData) {
          if (!comp.multiViewDataCache[msgId]) comp.multiViewDataCache[msgId] = {};
          comp.multiViewDataCache[msgId][view.mcode] = opData;
        }
      }

      if (opData) {
        // [FIX] Ensure trendline data is ALWAYS present for Snapshot (mcode=1),
        // whether we pulled opData from cache (which might be missing it) or the API fallback.
        if (view.mcode === 1) {
          const analyticalSource = targetBlock?.analyticalData || targetBlock?.parsedData;
          const sourceForTrend = result?.trendline_response ? result :
            (analyticalSource?.trendline_response ? analyticalSource :
              (targetBlock?.filteredWidgetData?.trendline_response ? targetBlock.filteredWidgetData :
                (targetBlock?.builtCustomData?.trendline_response ? targetBlock.builtCustomData :
                  (analyticalSource?.GetCustomSnapshotWidget?.trendline_response ? analyticalSource.GetCustomSnapshotWidget : null))));

          if (sourceForTrend?.trendline_response && !opData.trendline_response) {
            opData.trendline_response = sourceForTrend.trendline_response;
          } else if (comp.currentTrendlineResponse && !opData.trendline_response) {
            opData.trendline_response = comp.currentTrendlineResponse;
          }

          if (sourceForTrend?.response_payload_trendline && !opData.response_payload_trendline) {
            opData.response_payload_trendline = sourceForTrend.response_payload_trendline;
          } else if (comp.currentRowPayloadTrendline && !opData.response_payload_trendline) {
            opData.response_payload_trendline = comp.currentRowPayloadTrendline;
          }

          const sourceForPayload = result?.response_payload ? result :
            (analyticalSource?.response_payload ? analyticalSource :
              (targetBlock?.filteredWidgetData?.response_payload ? targetBlock.filteredWidgetData :
                (targetBlock?.builtCustomData?.response_payload ? targetBlock.builtCustomData :
                  (analyticalSource?.GetCustomSnapshotWidget?.response_payload ? analyticalSource.GetCustomSnapshotWidget : null))));

          if (sourceForPayload?.response_payload && !opData.response_payload) {
            opData.response_payload = sourceForPayload.response_payload;
          }
        }
        
        // --- ISOLATION START ---
        // processStep2Response modifies global comp state. If we are switching an old block while a new one is loading,
        // we MUST preserve the global state so the new question is not corrupted.
        const savedState: any = {};
        if (isSwitchingOldBlockDuringLoad) {
          savedState.mcode = comp.mcode;
          savedState.activeView = comp.activeView;
          savedState.arktabaccess = comp.arktabaccess;
          savedState.isBrandAnalysis = comp.isBrandAnalysis;
          savedState.is_sigtesting = comp.is_sigtesting;
          savedState.isSig = comp.isSig;
          savedState.promptData = comp.promptData;
          savedState.isTotalBase = comp.isTotalBase;
          savedState.selectedWeight = comp.selectedWeight;
          savedState.DefaultWeight = comp.DefaultWeight;
          savedState.lastCrosstabPayload = comp.lastCrosstabPayload;
          savedState.lastSnapshotPayload = comp.lastSnapshotPayload;
          savedState.lastBrandAnalysisPayload = comp.lastBrandAnalysisPayload;
          savedState.lastBrandAnalysisOriginalPipes = comp.lastBrandAnalysisOriginalPipes;
          savedState.currentRowPayloadTrendline = comp.currentRowPayloadTrendline;
          savedState.currentTrendlineResponse = comp.currentTrendlineResponse;
          savedState.currentCustomizationTrendline = comp.currentCustomizationTrendline;
          savedState.outputSnapshotData = comp._configService.outputSnapshotData;
          savedState.standardSnapOutputData = comp._configService.standardSnapOutputData;
          savedState.snapshotConfiguration = comp.snapshotConfiguration;
          savedState.showSnapshot = comp.showSnapshot;
          savedState.displayTable = comp.displayTable;
          savedState.isDiagnostic = comp.isDiagnostic;
          savedState.MainResult = comp.MainResult;
          savedState.OutputData = comp.OutputData;
          savedState.filteredWidgetData = comp.filteredWidgetData;
          savedState.filteredWidgetID = comp.filteredWidgetID;
          savedState.CrossTabOutPutEntireData = comp.CrossTabOutPutEntireData;
          savedState.isTranspose = comp.isTranspose;
          savedState.CrossTabOutPutData = comp.CrossTabOutPutData;
          savedState.isCreateBtnClick = comp.isCreateBtnClick;
          savedState.selectionSummary = comp.selectionSummary;
          
          // Temporarily set mcode so processStep2Response branches correctly for the old block
          comp.mcode = view.mcode;
        }

        comp.processStep2Response(opData, result, true, targetBlock);

        if (isSwitchingOldBlockDuringLoad) {
          Object.assign(comp, savedState);
          comp._configService.outputSnapshotData = savedState.outputSnapshotData;
          comp._configService.standardSnapOutputData = savedState.standardSnapOutputData;
        }
        // --- ISOLATION END ---

        comp.chatPreviewBlocks = [...comp.chatPreviewBlocks];

        const innerMsgId = targetBlock?.messageId || comp.selectedMessageId || 'current';

        // 2. INSIGHT SYNCHRONIZATION: Wait if insights are currently being pre-fetched in background
        if (!comp.multiViewInsightsCache[innerMsgId] || !comp.multiViewInsightsCache[innerMsgId][view.mcode]) {
          let insAttempts = 0;
          while (insAttempts < 100 && (!comp.multiViewInsightsCache[innerMsgId] || !comp.multiViewInsightsCache[innerMsgId][view.mcode]) && (comp.inFlightInsights[innerMsgId]?.has(view.mcode))) {
            await new Promise(r => setTimeout(r, 100)); // Wait 100ms
            insAttempts++;
          }
        }

        // Check cache for insights/QnA
        const insightsCache = comp.multiViewInsightsCache[innerMsgId] || comp.multiViewInsightsCache['current'];

        if (insightsCache && insightsCache[view.mcode]) {
          const cachedData = insightsCache[view.mcode];

          // Apply insights
          comp.insightsVal = cachedData.insightsVal || [];
          comp.insightsStreamingText = ''; // Clear streaming to show completed state
          comp.isInsightsStreaming = false;
          comp.isInsightspopupOpen = true;

          // Special handling for Snapshot dual-view (Standard vs Trend)
          if (view.mcode === 1 && cachedData.rawInsights) {
            comp.snapshotinsights_standard_text = cachedData.standard_insights || '';
            comp.snapshotinsights_trendline_text = cachedData.trend_insights || '';

            if (!comp.snapshotinsights_standard_text && cachedData.rawInsights.includes('[[[SPLIT]]]')) {
              const parts = cachedData.rawInsights.split('[[[SPLIT]]]');
              comp.snapshotinsights_standard_text = parts[0];
              comp.snapshotinsights_trendline_text = parts[1];
            }

            const activeSnapshotText = (targetBlock?.isTrendActive ?? comp.isTrendActive)
              ? (comp.snapshotinsights_trendline_text || comp.snapshotinsights_standard_text || cachedData.rawInsights)
              : (comp.snapshotinsights_standard_text || cachedData.rawInsights);
            comp.insightsVal = comp.formatInsightsIntoPoints(activeSnapshotText);
          }

          if (!isSwitchingOldBlockDuringLoad) comp.stopAiLoader();

          // Update UI components
          comp.updateInsightsPopupFinal();
          comp.updateMarkdownTextRealTime(cachedData.qnaText);

          // Ensure the QnA area is visible
          comp.isChatAreaVisible = false;

        } else {
          // If not in cache and not in-flight, it's a fallback scenario
          console.warn(`[MULTI-VIEW] No cached insights found for mcode: ${comp.mcode}.`);
          const markdownData = view.mcode === 1 ? comp.buildSnapshotMarkdownForAPI() : comp.buildCrosstabMarkdownForAPI();
          const widget_details = view.mcode === 1 ? comp.filteredWidgetData : {};
          comp.callInsightsAPI(view.mcode, markdownData, comp.currentQuestion, widget_details, targetBlock);
        }
      }
    } catch (err) {
      console.error('[MULTI-VIEW ERROR] Failed to switch view:', err);
      comp.setFailedCaseState(true, targetBlock);
      comp.errorHeader = 'Error: Failed to load module data';
    } finally {
      comp.isAnalysisSwitching = false;
      if (targetBlock) targetBlock.isAnalysisSwitching = false;
      if (!isSwitchingOldBlockDuringLoad) comp.isInsightsStreaming = false;
      comp._loaderService.hideLoader();
    }
  }

}
